import { useEffect, useState, type FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { deleteNewsletter, saveNewsletter as saveNewsletterRequest } from "@client/admin/api"
import type { NewsletterRow } from "@client/admin/types"
import { useEntitiesStore, useRows } from "@client/store/entities"
import { RichTextEditor } from "@client/components/richtext"
import { useConfirm } from "./components/confirm-modal"
import { ContentNav, ContentStatusBadge } from "./content-components"
import { useHasPermission } from "./session-context"

function inputClass(extra = "") {
  return `w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-3 text-sm text-ink placeholder:text-ink/40 transition-colors focus:border-forest-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${extra}`
}

export function NewsletterEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === "new" || !id
  const newsletterState = useRows("newsletter", !isNew)
  const loadedNewsletters = newsletterState.rows as NewsletterRow[] | null
  const newsletters = loadedNewsletters ?? []
  const optimisticUpdate = useEntitiesStore((state) => state.optimisticUpdate)
  const optimisticCreate = useEntitiesStore((state) => state.optimisticCreate)
  const optimisticRemove = useEntitiesStore((state) => state.optimisticRemove)
  const canMutate = useHasPermission("content_mutate")

  const hydrating = !isNew && loadedNewsletters === null
  const existing = isNew ? null : (newsletters.find((row) => row.id === id) ?? null)

  const [title, setTitle] = useState("")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const { confirm, modal } = useConfirm()

  useEffect(() => {
    if (existing) {
      setTitle(existing.title)
      setSubject(existing.subject)
      setBody(existing.body)
    }
  }, [existing?.id])

  const save = async (status: "draft" | "sent") => {
    if (!canMutate) return
    setSaving(true)
    setError(null)
      try {
        if (!title.trim() || !subject.trim() || !bodyText().trim()) {
          setError("Title, subject and body are required.")
          return
        }
      const now = new Date().toISOString()
      const row: NewsletterRow = {
        id: existing?.id ?? crypto.randomUUID(),
        title: title.trim(),
        subject: subject.trim(),
        body,
        status,
        sentAt: status === "sent" ? now : existing?.sentAt ?? null,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        deletedAt: null,
      }
      const request = () =>
        saveNewsletterRequest(row.id, {
          title: row.title,
          subject: row.subject,
          body: row.body,
          status: row.status,
        })
      const mutation = existing
        ? optimisticUpdate("newsletter", row.id, row, request)
        : optimisticCreate("newsletter", row, request)
      navigate("/admin/newsletters", { replace: true })
      await mutation
      toast.success(status === "sent" ? "Newsletter sent" : "Draft saved")
    } catch {
      setError("Could not save the newsletter.")
      toast.error("Could not save the newsletter.")
    } finally {
      setSaving(false)
    }
  }

  const send = async (event: FormEvent) => {
    event.preventDefault()
    if (!canMutate) return
    if (existing?.status === "sent") {
      navigate("/admin/newsletters")
      return
    }
    const ok = await confirm({
      title: "Send newsletter",
      message: "Send this newsletter to every subscribed subscriber? This cannot be undone.",
      confirmLabel: "Send",
    })
    if (ok) void save("sent")
  }

  const removeNewsletter = async () => {
    if (!existing) return
    const ok = await confirm({
      title: "Delete newsletter",
      message: `Delete "${existing.title}"? This cannot be undone.`,
      confirmLabel: "Delete",
    })
    if (!ok) return
    try {
      await optimisticRemove("newsletter", existing.id, () => deleteNewsletter(existing.id))
      navigate("/admin/newsletters", { replace: true })
      toast.success("Newsletter deleted")
    } catch {
      toast.error("Could not delete the newsletter.")
    }
  }

  const isSent = existing?.status === "sent"

  /** WYSIWYG bodies are HTML — validate on the visible text. */
  const bodyText = () => body.replace(/<[^>]*>/g, "").trim()

  if (hydrating) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="h-10 w-64 animate-pulse rounded bg-cream-100" />
        <div className="h-3 w-72 animate-pulse rounded bg-cream-100" />
        <div className="h-96 animate-pulse rounded-[10px] border border-gold-500 bg-white" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <ContentNav />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">
            {isSent ? "Newsletter" : isNew ? "New newsletter" : "Edit newsletter"}
          </h1>
          <p className="mt-1 text-sm text-ink/60">
            {isSent ? "Review the newsletter that was sent to subscribers." : "Write an update for the Redempta community."}
          </p>
        </div>
        {existing && <ContentStatusBadge value={existing.status} />}
      </div>

      <form onSubmit={send} className="space-y-6 rounded-[10px] border border-gold-500 bg-white p-5 sm:p-8">
        <div className="space-y-2">
          <label className="text-sm font-medium text-ink">
            Internal title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isSent || !canMutate}
            placeholder="June Impact Update"
            className={inputClass("w-full")}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-ink">
            Email subject
          </label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSent || !canMutate}
            placeholder="Redempta: what we achieved this month"
            className={inputClass("w-full")}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-ink">
            Body <span className="font-normal text-ink/50">(rich text rendered in the email)</span>
          </label>
          <RichTextEditor
            key={existing?.id ?? "new"}
            initialValue={existing?.body ?? ""}
            onChange={setBody}
            disabled={isSent || !canMutate}
          />
        </div>

        {error && <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        <div className="flex flex-col gap-3 border-t border-gold-500/40 pt-5 sm:flex-row sm:items-center">
          {!isSent && canMutate && (
            <button
              type="button"
              onClick={() => void save("draft")}
              disabled={saving}
              className="w-full rounded-full border border-gold-500 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100 disabled:opacity-50 sm:w-auto"
            >
              {saving ? "Saving..." : "Save draft"}
            </button>
          )}
          {isSent ? (
            <button
              type="button"
              onClick={() => navigate("/admin/newsletters")}
              className="w-full rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 sm:w-auto"
            >
              Back to list
            </button>
          ) : canMutate ? (
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {saving ? "Sending..." : "Send newsletter"}
            </button>
          ) : null}
          {!isSent && !isNew && canMutate && (
            <button
              type="button"
              onClick={() => void removeNewsletter()}
              className="w-full rounded-full px-4 py-2 text-left text-sm font-medium text-red-700 transition-colors hover:bg-red-50 sm:ml-auto sm:w-auto sm:text-right"
            >
              Delete
            </button>
          )}
        </div>
      </form>
      {modal}
    </div>
  )
}
